/**
 * =============================================================================
 * IDS CRM Web - Engineer Rates (Batch C rate model)
 * =============================================================================
 *
 * Effective-dated cost/charge/callout/mileage rates. The company default
 * (no engineer) applies to everyone without a specific row. History is
 * append-only; completed work logs snapshot rates so past costs never move.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatMoney } from '../utils/money';
import { formatDateKey, useUkTodayDefault } from '../utils/dates';

// Everything but the effective date, which comes from useUkTodayDefault: this
// object used to be built once when the module loaded, with toISOString, so an
// admin who left the page open offered a stale date, and in British Summer Time
// between midnight and 01:00 it offered yesterday - which is inside a pay
// period Finance may already have paid (ONCALL_PAY_FIXES_DESIGN §8, B2).
const EMPTY_FIELDS = {
  engineer_id: '',
  hourly_cost_rate: '',
  hourly_charge_rate: '',
  callout_fee: '',
  ooh_remote_fee: '',
  mileage_rate: '',
  mileage_cost_rate: '',
  notes: ''
};

function RateSettings() {
  const { authFetch, user } = useAuth();
  const effectiveFrom = useUkTodayDefault();
  const [rates, setRates] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FIELDS);
  const [saving, setSaving] = useState(false);
  const canEdit = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ratesRes, engineersRes] = await Promise.all([
        authFetch('/api/v1/admin/rates'),
        authFetch('/api/v1/engineers?include_unassignable=true&include_inactive=true')
      ]);
      if (ratesRes.ok) {
        const data = await ratesRes.json();
        setRates(data.data || []);
      }
      if (engineersRes.ok) {
        const data = await engineersRes.json();
        setEngineers(data.data || []);
      }
    } catch (err) {
      setError('Failed to load rates');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toMinor = (value) => Math.round((Number(value) || 0) * 100);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch('/api/v1/admin/rates', {
        method: 'POST',
        body: JSON.stringify({
          engineer_id: form.engineer_id || null,
          hourly_cost_rate_minor_units: toMinor(form.hourly_cost_rate),
          hourly_charge_rate_minor_units: toMinor(form.hourly_charge_rate),
          callout_fee_minor_units: toMinor(form.callout_fee),
          ooh_remote_fee_minor_units: toMinor(form.ooh_remote_fee),
          mileage_rate_minor_units: toMinor(form.mileage_rate),
          mileage_cost_rate_minor_units: form.mileage_cost_rate === '' ? null : toMinor(form.mileage_cost_rate),
          // Recomputed here, so a form open since before midnight still
          // dates the change today rather than yesterday.
          effective_from: effectiveFrom.refresh(),
          notes: form.notes || null
        })
      });
      if (response.ok) {
        setForm(EMPTY_FIELDS);
        effectiveFrom.reset();
        fetchData();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.message || 'Failed to add rate');
      }
    } catch (err) {
      setError('Failed to add rate');
    } finally {
      setSaving(false);
    }
  };

  const removeRate = async (rateId) => {
    const response = await authFetch(`/api/v1/admin/rates/${rateId}`, { method: 'DELETE' });
    if (response.ok) {
      fetchData();
    } else {
      const data = await response.json().catch(() => ({}));
      setError(data.message || 'Failed to delete rate');
    }
  };

  const hasPlaceholder = rates.some(rate => /PLACEHOLDER/i.test(rate.notes || ''));

  return (
    <div className="admin-section">
      <h3>Engineer Rates</h3>
      <p className="section-description">
        Hourly cost (internal), hourly charge (customer-facing), the out-of-hours incident payment
        (paid to the engineer per ticket per night), the OOH remote fee (charged to billable
        customers for a remotely-resolved out-of-hours incident) and the two mileage rates:
        cost (what a mile costs the business — prices the automatic travel estimates) and
        billable (reserved for customer charging, not yet applied anywhere).
        The company default applies to engineers without a specific row; newer effective dates win.
        Completed work logs snapshot their rates, so changing rates never rewrites past costs.
      </p>

      {hasPlaceholder && (
        <div className="rates-placeholder-warning">
          ⚠ The seeded company default uses PLACEHOLDER values — confirm real rates before
          building invoicing on these numbers.
        </div>
      )}

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="loading">Loading rates...</div>
      ) : (
        <table className="admin-table rates-table">
          <thead>
            <tr>
              <th>Applies To</th>
              <th>Cost / h</th>
              <th>Charge / h</th>
              <th>Incident (paid)</th>
              <th>OOH Remote (charged)</th>
              <th>Mileage cost / mi</th>
              <th>Mileage billable / mi</th>
              <th>Effective From</th>
              <th>Notes</th>
              {canEdit && <th></th>}
            </tr>
          </thead>
          <tbody>
            {rates.map(rate => (
              <tr key={rate.rate_id} className={rate.engineer_id ? '' : 'company-default-row'}>
                <td>{rate.engineer_name || 'Company default'}</td>
                <td>{formatMoney(rate.hourly_cost_rate_minor_units)}</td>
                <td>{formatMoney(rate.hourly_charge_rate_minor_units)}</td>
                <td>{formatMoney(rate.callout_fee_minor_units)}</td>
                <td>{formatMoney(rate.ooh_remote_fee_minor_units)}</td>
                <td>{rate.mileage_cost_rate_minor_units != null ? formatMoney(rate.mileage_cost_rate_minor_units) : '—'}</td>
                <td>{formatMoney(rate.mileage_rate_minor_units)}</td>
                <td>{formatDateKey(rate.effective_from)}</td>
                <td className="rate-notes">{rate.notes || '-'}</td>
                {canEdit && (
                  <td>
                    <button className="btn-small-danger" onClick={() => removeRate(rate.rate_id)}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canEdit && (
        <form className="rate-add-form" onSubmit={submit}>
          <h4>Add Rate</h4>
          <div className="rate-form-grid">
            <div className="form-group">
              <label>Engineer (blank = company default)</label>
              <select value={form.engineer_id} onChange={(e) => setForm(prev => ({ ...prev, engineer_id: e.target.value }))}>
                <option value="">Company default</option>
                {engineers.map(engineer => (
                  <option key={engineer.engineer_id} value={engineer.engineer_id}>{engineer.full_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Hourly cost (£) *</label>
              <input type="number" min="0" step="0.01" value={form.hourly_cost_rate} required
                onChange={(e) => setForm(prev => ({ ...prev, hourly_cost_rate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Hourly charge (£) *</label>
              <input type="number" min="0" step="0.01" value={form.hourly_charge_rate} required
                onChange={(e) => setForm(prev => ({ ...prev, hourly_charge_rate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Incident payment (£, paid to engineer)</label>
              <input type="number" min="0" step="0.01" value={form.callout_fee}
                onChange={(e) => setForm(prev => ({ ...prev, callout_fee: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>OOH remote fee (£, charged)</label>
              <input type="number" min="0" step="0.01" value={form.ooh_remote_fee}
                onChange={(e) => setForm(prev => ({ ...prev, ooh_remote_fee: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Mileage cost (£/mi, internal)</label>
              <input type="number" min="0" step="0.01" value={form.mileage_cost_rate}
                onChange={(e) => setForm(prev => ({ ...prev, mileage_cost_rate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Mileage billable (£/mi, not yet charged)</label>
              <input type="number" min="0" step="0.01" value={form.mileage_rate}
                onChange={(e) => setForm(prev => ({ ...prev, mileage_rate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label htmlFor="rate-effective-from">Effective from *</label>
              <input id="rate-effective-from" type="date" value={effectiveFrom.value} required
                onChange={(e) => effectiveFrom.setValue(e.target.value)} />
            </div>
            <div className="form-group rate-notes-field">
              <label>Notes</label>
              <input type="text" value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Add Rate'}
          </button>
        </form>
      )}
    </div>
  );
}

export default RateSettings;
